import { Link } from "react-router-dom";
import { images } from "../../assets/assets";
import Navbar from "../../Components/Navbar";
import { CalendarDays, MoveRight, Phone, Tag, User } from "lucide-react";
import Footer from "../../Components/Footer";
import { getImageUrl } from "../../utils/getImageUrl";
import { useState, useEffect } from "react";
import { getAllBlogs } from "../../api";

// Format date from createdAt to "Dec 8, 2024" format
const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Skeleton Loading Component
const BlogSkeleton = () => (
  <div className="group flex-shrink-0 p-2 rounded-[12px] bg-gray-200 animate-pulse">
    <div className="h-[160px] bg-gray-300 rounded-[8px] mb-2" />
    <div className="h-4 bg-gray-300 rounded mb-2 w-16" />
    <div className="h-5 bg-gray-300 rounded mb-2" />
    <div className="h-4 bg-gray-300 rounded mb-4 w-full" />
    <div className="flex gap-3 mb-3">
      <div className="h-4 bg-gray-300 rounded w-24" />
      <div className="h-4 bg-gray-300 rounded w-24" />
    </div>
    <div className="h-4 bg-gray-300 rounded w-20" />
  </div>
);

const Blog = () => {
  const [BlogData, setBlogData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  useEffect(() => {
    const fetchBlogs = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await getAllBlogs();
        const blogsArr = Array.isArray(response)
          ? response
          : Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response?.blogs)
          ? response.blogs
          : [];
        setBlogData(blogsArr);
      } catch (err) {
        setError("Failed to load blogs. Please try again later.");
        console.error("Error fetching blogs:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchBlogs();
  }, []);

  const safeBlogs = Array.isArray(BlogData) ? BlogData : [];

  return (
    <div className="font-lato">
      <Navbar />
      {/* Top Page heading banner */}
      <div className="bg-[#F7F7F7B2] py-4 sm:py-6 flex justify-center px-4">
        <div className="flex flex-col sm:flex-row justify-center sm:justify-start gap-2 sm:gap-4 items-center max-w-[1100px]">
          <img
            src={images.Logo}
            alt="logo"
            className="w-[50px] h-[50px] sm:w-[80px] sm:h-[80px] md:w-[95px] md:h-[95px] flex-shrink-0"
          />
          <div className="max-w-[500px]">
            <p className="font-bold text-[16px] sm:text-[20px] md:text-[22px] text-center sm:text-left">
              Our Health Blog
            </p>
            <p className="font-openSans text-[12px] sm:text-[14px] md:text-[16px] leading-[1.2] text-center sm:text-left">
              Stay informed with the latest health tips, medical insights, and
              wellness advice from Dev Rishi Hospital. Our blog is designed to
              educate, guide, and empower you to make better healthcare
              decisions for yourself and your family.
            </p>
          </div>
        </div>
      </div>
      <div className="max-w-[1200px] mx-auto px-4 overflow-hidden grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 my-4">
        {error ? (
          <div className="col-span-full py-12 text-center">
            <p className="text-red-500 font-poppins font-semibold text-[16px]">
              {error}
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 bg-[#1975FF] text-white px-6 py-2 rounded-[10px] hover:bg-[#1560d4] transition-all"
            >
              Retry
            </button>
          </div>
        ) : loading ? (
          <>
            {[...Array(4)].map((_, idx) => (
              <BlogSkeleton key={idx} />
            ))}
          </>
        ) : safeBlogs.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <p className="font-poppins text-[16px] text-gray-600">
              No blogs found
            </p>
          </div>
        ) : (
          safeBlogs.map((blog, idx) => (
            <Link
              key={idx}
              to={`/blog-detail/${blog?.slug}`}
              className="group flex-shrink-0 p-2 rounded-[12px] shadow-[0px_0px_25px_0px_#BDBDBD33] bg-white hover:shadow-[0px_4px_30px_0px_#BDBDBD66] hover:-translate-y-1 active:translate-y-0 active:shadow-[0px_0px_25px_0px_#BDBDBD33] transition-all duration-250 ease-in-out"
            >
              <div className="overflow-hidden rounded-[8px] mb-2">
                <img
                  src={getImageUrl(blog.imagePath)}
                  alt={`blog-${idx}`}
                  className="w-full h-[160px] object-cover group-hover:scale-105 transition-transform duration-400 ease-in-out"
                />
              </div>
              <div className="flex items-center gap-1 mb-2">
                <Tag
                  color="#D4AF37"
                  className="w-[14px] h-[14px] flex-shrink-0"
                />
                <p className="font-poppins font-medium text-[13px] text-[#D4AF37]">
                  {blog.category}
                </p>
              </div>
              <h3 className="font-poppins font-semibold text-[14px] md:text-base leading-[1.25] mb-2">
                {blog.heading}
              </h3>
              <p className="font-poppins text-[13px] text-gray-600">
                {blog.blogSmallDesc}
              </p>
              <div className="flex items-center gap-3 px-1 flex-wrap mt-4">
                <div className="flex items-center gap-1">
                  <User color="#515151" className="w-[14px] h-[14px]" />
                  <p className="text-[#515151] font-poppins font-medium text-[12px]">
                    {blog.author?.name}
                  </p>
                </div>
                <div className="flex items-center gap-1">
                  <CalendarDays color="#515151" className="w-[14px] h-[14px]" />
                  <p className="text-[#515151] font-poppins font-medium text-[12px]">
                    {formatDate(blog.createdAt)}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 px-1 mt-3">
                <p className="text-[12px] font-medium font-poppins text-[#0088FF] group-hover:text-[#0060cc] transition-colors">
                  Read More
                </p>
                <MoveRight
                  color="#0088FF"
                  className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-200"
                />
              </div>
            </Link>
          ))
        )}
      </div>
      <div className="py-10 md:py-14 bg-[#F7F7F7B2]    transition-all duration-500   hover:bg-[#F0EBFFB2] border border-transparent hover:border-white my-10 md:my-12">
        <h1 className="max-w-[550px] text-center text-[22px] sm:text-[26px] md:text-[28px] lg:text-[32px] font-semibold mx-auto leading-[1.3]">
          Have Health Questions?
        </h1>
        <p className="max-w-[375px] font-openSans text-[14px] sm:text-[16px] md:text-[17px] lg:text-[18px] mx-auto text-center mt-3">
          Consult with our expert doctors for personalized medical advice.
        </p>
        <div className="flex flex-wrap gap-4 justify-center mt-8">
          <Link
            to="/appointment"
            className="bg-[#1975FF] rounded-[10px] py-1 pr-1 pl-4 flex items-center gap-2 text-[14px] font-semibold text-white hover:bg-[#1560d4] active:bg-[#1050b8] active:scale-95 transition-all duration-200 shadow-md hover:shadow-lg group"
          >
            <Phone color="#fff" className=" w-5 h-5" />
            Book a Consultation Today{" "}
            <span className="p-2 bg-white rounded-full transition-transform duration-300 group-hover:scale-110">
              <MoveRight color="#000" className="rotate-[-38deg] w-4 h-4" />
            </span>
          </Link>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Blog;
